/**
 * Live model-capability governance broadcasts — run lifecycle, admissions,
 * the two selection registers, the catalogue and the brokered work plane —
 * delivered through the shared `global_events` room (PRD-0243 D-14,
 * PRD-0254 D-11).
 *
 * This domain exists because that room has exactly one entry point: a
 * client-emitted `subscribe_global`. `websocketManager.subscribe()` only
 * attaches a local handler — it never sends the join and never replays it
 * after a reconnect — so a console built on that alone would compile, run,
 * and silently receive nothing.
 *
 * One registry over all eight literals rather than one per hook constant: the
 * registry split in `smem.ts` exists to keep disjoint *consumer* hooks from
 * running on each other's bursts, and here a single hook consumes all eight
 * and routes them to different query keys by event name. Splitting would fan
 * one shared room into five effects that must then be kept in step for no
 * isolation gain.
 */

// The leaf rather than the barrel: MODEL_CAPABILITY_EVENTS is a runtime value,
// and importing it through @/types/modelCapabilities puts that whole re-export
// — otherwise pure types, erased at build — into the dev module graph, beneath
// the websocket client the entire console depends on. One constant taken from
// the barrel therefore rebuilds most of the app on every edit to it.
import {
  MODEL_CAPABILITY_EVENTS,
  type ModelCapabilityEventName,
  type ModelCapabilityEventPayload,
} from '@/types/modelCapabilities/events'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

/** What one dispatch carries: which broadcast fired, and its payload. */
type ModelCapabilityDispatch = [
  ModelCapabilityEventName,
  ModelCapabilityEventPayload,
]

export class ModelCapabilitiesLiveApi {
  private readonly events: SubscriptionRegistry<null, ModelCapabilityDispatch>

  constructor(private readonly client: WebSocketClient) {
    this.events = new SubscriptionRegistry({
      // Joins the shared `global_events` room; subscribe_global is idempotent
      // server-side. There is deliberately no leave — the activity feed, the
      // summariser console and the registry domains share the room, and
      // leaving would break those subscribers.
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
  }

  /**
   * Subscribe to every governance broadcast through one callback, which
   * receives the event name alongside the payload. The consumer routes cache
   * invalidation by event name and by payload ids, so the name has to travel
   * with the frame.
   */
  subscribeToModelCapabilityEvents(
    callback: (
      event: ModelCapabilityEventName,
      payload: ModelCapabilityEventPayload
    ) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of MODEL_CAPABILITY_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        // The one transport-boundary assertion: Socket.IO hands every
        // argument through as `unknown`, and these payloads are identity-only
        // routing data — no field is ever rendered, they only choose which
        // query keys to invalidate — so the consumer's narrowing by event
        // name is the whole contract.
        this.events.dispatch(null, [event, raw as ModelCapabilityEventPayload])
      })
    }

    return this.events.subscribe(null, ([name, payload]) =>
      callback(name, payload)
    )
  }

  /** Re-join global_events for every live listener after a reconnect. */
  replay(): void {
    this.events.replay()
  }

  clear(): void {
    this.events.clear()
  }
}
